import { Injectable, OnDestroy } from '@angular/core'
import { LogService } from 'htv-lib'
import { ALARM_CONFIG } from '../constants/alarm.constants'
import { VolumeService } from './volume.service'

interface AlarmSound {
  oscillator: OscillatorNode
  gainNode: GainNode
}

@Injectable({
  providedIn: 'root',
})
export class AudioAlarmService implements OnDestroy {
  private audioContext?: AudioContext
  private alarmSound?: AlarmSound
  private noteInterval?: number
  private currentNoteIndex = 0
  private previousVolume?: number
  private previousMuted?: boolean

  constructor(
    private logService: LogService,
    private volumeService: VolumeService
  ) {}

  async startAlarm(): Promise<void> {
    try {
      this.setAlarmVolume()

      this.audioContext = new AudioContext()
      await this.ensureAudioReady(this.audioContext)
      this.initializeSound(this.audioContext)
      this.startNoteSequence()
    } catch (error) {
      this.logService.error(`Could not start alarm: ${error}`)
      throw error
    }
  }

  stopAlarm(): void {
    this.cleanupAudio()
    this.restorePreviousVolume()
  }

  ngOnDestroy(): void {
    this.cleanupAudio()
    this.restorePreviousVolume()
  }

  private async ensureAudioReady(context: AudioContext): Promise<void> {
    if (context.state === 'suspended') {
      await context.resume()
    }
  }

  private initializeSound(audioContext: AudioContext): void {
    const oscillator = audioContext.createOscillator()
    const gainNode = audioContext.createGain()

    oscillator.type = 'sine'
    oscillator.frequency.value = ALARM_CONFIG.AUDIO.NOTES.A4
    gainNode.gain.value = ALARM_CONFIG.AUDIO.INITIAL_VOLUME

    oscillator.connect(gainNode)
    gainNode.connect(audioContext.destination)
    oscillator.start()

    this.alarmSound = { oscillator, gainNode }
  }

  private startNoteSequence(): void {
    this.playNextNote()
    this.noteInterval = window.setInterval(() => {
      this.playNextNote()
    }, ALARM_CONFIG.INTERVALS.NOTE_CHANGE_MS)
  }

  private playNextNote(): void {
    if (!this.alarmSound) return

    const note = ALARM_CONFIG.AUDIO.SEQUENCE[this.currentNoteIndex]
    this.alarmSound.oscillator.frequency.setValueAtTime(note, this.audioContext?.currentTime || 0)

    this.currentNoteIndex = (this.currentNoteIndex + 1) % ALARM_CONFIG.AUDIO.SEQUENCE.length
  }

  private cleanupAudio(): void {
    this.stopNoteSequence()
    this.disconnectOscillator()
    this.closeAudioContext()
  }

  private stopNoteSequence(): void {
    if (this.noteInterval) {
      clearInterval(this.noteInterval)
      this.noteInterval = undefined
    }
  }

  private disconnectOscillator(): void {
    if (this.alarmSound) {
      try {
        this.alarmSound.oscillator.stop()
        this.alarmSound.oscillator.disconnect()
        this.alarmSound.gainNode.disconnect()
      } catch (error) {
        this.logService.debug(`Error stopping oscillator: ${error}`)
      }
      this.alarmSound = undefined
    }
  }

  private closeAudioContext(): void {
    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = undefined
    }
    this.currentNoteIndex = 0
  }

  private setAlarmVolume(): void {
    this.previousVolume = this.volumeService.volume
    this.previousMuted = this.volumeService.muted

    this.volumeService.setVolumeMute(false)
    this.volumeService.setVolume(ALARM_CONFIG.AUDIO.TV_ALARM_VOLUME)
  }

  private restorePreviousVolume(): void {
    if (this.previousVolume !== undefined) {
      this.volumeService.setVolume(this.previousVolume)
    }
    if (this.previousMuted !== undefined) {
      this.volumeService.setVolumeMute(this.previousMuted)
    }

    this.previousVolume = undefined
    this.previousMuted = undefined
  }
}
